import { h, Component } from 'preact';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import CSSTransition from 'react-transition-group/CSSTransition';
// AutoSuggest Map
import { getAutoSuggest } from '../../../../utilities/form/autoSuggest/autoSuggestMap';

// Components
import Label from '../../../Form/Label';
import Description from '../../../Form/Description';
import Error from '../../../Form/Error';
import Icon from '../../../Global/Icon';
import Text from '../../../Global/Text';

import { actionHandler } from '../../../../utilities/form/formUtils';
import { replaceAll } from '../../../../utilities/common';

class AutoSuggest extends Component {
  constructor(props) {
    super(props);
    const { variant, values, id } = this.props;
    this.onAutoSuggestChange = this.onAutoSuggestChange.bind(this);
    this.onAutoSuggestBlur = this.onAutoSuggestBlur.bind(this);
    this.onKeyPressEnter = this.onKeyPressEnter.bind(this);
    this.actionChange = this.actionChange.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.init(variant, values, id);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.isValueSet = this.isValueSet.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.getHintMessage = this.getHintMessage.bind(this);
  }

  onAutoSuggestChange(event, { newValue }) {
    const { setValues, values } = this.props;
    this.autoSuggestObject.onChangeEvent(event, newValue, this, setValues, values);
    this.getHintMessage();
    const caret = event.target.selectionStart;
    const element = event.target;
    window.requestAnimationFrame(() => {
      if (element.selectionStart) {
        element.selectionStart = caret;
        element.selectionEnd = caret;
      }
    });
  }

  // Autosuggest will call this function every time you need to update suggestions.
  onSuggestionsFetchRequested({ value }) {
    const { dispatch, setValues, values } = this.props;
    this.autoSuggestObject.onSuggestionsFetchRequested(value, this, dispatch, setValues, values);
    this.getHintMessage();
  }

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    const { setValues, values } = this.props;
    this.autoSuggestObject.onSuggestionsClearRequested(this, setValues, values);
    this.getHintMessage();
  }

  onAutoSuggestBlur(event, { highlightedSuggestion }) {
    const { onBlur, setValues, values, id } = this.props;
    const { hints, suggestions } = this.state;
    let newHighlightedSuggestion = highlightedSuggestion;
    onBlur(event);
    this.state.isFocused = false;

    if (suggestions.length === 1) {
      [newHighlightedSuggestion] = suggestions;
    }

    if (hints[id]) {
      delete hints[id];
    }

    this.setState({
      isTyping: false,
      hints,
    });
    this.autoSuggestObject.onBlurEvent(this, newHighlightedSuggestion,
      setValues, values, this.props);

    if (newHighlightedSuggestion) {
      this.actionChange();
    }
  }

  onKeyPressEnter(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      this.onAutoSuggestBlur(event, { highlightedSuggestion: null });
    }
  }

  onSuggestionSelected(event, { method }) {
    if (method === 'enter') {
      // Prevent enter to submit the form.
      event.preventDefault();
    }
    this.getHintMessage();
  }

  onFocus() {
    this.state.isFocused = true;
    this.getHintMessage();
  }

  getHintMessage(suggestionValue) {
    const { value, hints, suggestions, isLoading, warnings } = this.state;
    const { id, touched, name, errors } = this.props;
    const { values } = this.props;
    let updateState = false;

    values[id] = suggestionValue || values[id];

    if (this.state.isFocused) {
      delete warnings[id];
      if ((!value || value.trim().length === 0)) {
        const msg = `Søg efter ${name}…`;
        updateState = hints[id] !== msg;
        hints[id] = msg;
      } else if (value && value.trim().length > 0
        && !isLoading && suggestions.length === 0) {
        const msg = `Ugyldig ${name}. Søg venligst igen`;
        updateState = hints[id] !== msg;
        hints[id] = msg;
      } else if (hints[id]) {
        delete hints[id];
        updateState = true;
      }
    } else {
      if (hints[id]) {
        delete hints[id];
        updateState = true;
      }

      if (suggestions.length !== 1 && !values[id]
        && !errors[id] && !warnings[id]) {
        warnings[id] = `Indtast et gyldigt ${name}`;
        touched[id] = true;
      } else if ((suggestions.length === 1 || values[id]) && warnings[id]) {
        delete warnings[id];
        updateState = true;
      }
    }

    if (updateState === true) {
      this.setState({ hints, warnings });
    }
  }

  getSuggestionValue(suggestion) {
    const { suggestions } = this.state;
    const { setValues, values } = this.props;
    let suggestionValue = suggestion;

    // Suggestion doesn't exist in IE, so making fallback to fallback from list
    if (!suggestion) {
      suggestionValue = this.autoSuggestObject.getSuggestionValue(
        suggestions[0],
        setValues,
        values,
        this.props
      );
    } else {
      suggestionValue = this.autoSuggestObject.getSuggestionValue(
        suggestion,
        setValues,
        values,
        this.props
      );
    }

    this.actionChange();
    this.getHintMessage(suggestionValue);
    return suggestionValue;
  }

  init(variant, values, id) {
    this.autoSuggestObject = getAutoSuggest(variant);
    let initValue = '';
    if (Object.hasOwnProperty.call(values, id)) {
      if (Object.hasOwnProperty.call(this.autoSuggestObject, 'getInitialValue')) {
        initValue = this.autoSuggestObject.getInitialValue(this.props);
        if (!initValue) {
          initValue = '';
        }
      }
      if (id.length > 0 && values[id]) {
        initValue = values[id];
      }
    }

    this.state = {
      value: initValue,
      suggestions: [],
      isTyping: false,
      hints: [],
      warnings: {},
      isLoading: false,
      isFocused: false,
    };
  }

  isValueSet() {
    const { id, values } = this.props;
    const { hints, warnings, isFocused } = this.state;

    return !isFocused && !hints[id] && !warnings[id] && values[id];
  }

  actionChange() {
    const { actionProp, dispatch, values, formValues, setValues } = this.props;
    let extraData = {};
    if (formValues && formValues.length > 0) {
      extraData = formValues.reduce((res, item) => {
        return { ...res, [item]: values[item] };
      }, {});
    }
    if (actionProp.length > 0) {
      actionHandler(actionProp, null, null, dispatch, { ...values, ...extraData }, setValues);
    }
  }

  renderSuggestion(suggestion) {
    const { id: name } = this.props;
    const { value } = this.state;

    return (
      <fieldset name={name} data-mf-replace-inner="**REMOVED**">
        <Text value={this.autoSuggestObject.renderSuggestion(suggestion)
          .replace(new RegExp(`(${value})`, 'i'), '<strong>$1</strong>')}
        />
      </fieldset>
    );
  }

  render() {
    const {
      id,
      label,
      desc,
      touched,
      placeholder,
      errors,
      required,
      columns,
      values,
      subType,
      successIcon,
      min,
      max,
      variant,
      actionProp,
      className,
      translations,
    } = this.props;

    // This is a bug fix when constructor sometimes doesn't get set.
    if (this.autoSuggestObject.type !== variant) {
      this.init(variant, values, id);
    }

    // Start loading initial values if needed.
    if (this.autoSuggestObject.loadInitialValues) {
      this.autoSuggestObject.loadInitialValues(this);
    }

    if (!this.state.isLoading && this.state.suggestions.length > 0 &&
      this.state.hints[id]) {
      this.getHintMessage();
    }

    const { suggestions, isLoading, warnings } = this.state;
    let { value } = this.state;
    const { shouldRenderSuggestions } = this.autoSuggestObject;
    const newValue = this.state.isTyping ? value : this.autoSuggestObject.getValue(values, this);

    if (!this.state.isTyping && value !== newValue) {
      this.setState({ value: newValue });
      this.getHintMessage();
    }
    value = newValue;

    const asClasses = this.autoSuggestObject.getClasses
      ? this.autoSuggestObject.getClasses(this)
      : [];
    const formFieldClass = classNames(
      'form__field',
      'form__field--autosuggest',
      'form__field--text',
      {
        [`form__field--${id}`]: id,
        [`form__field--${columns}col`]: columns,
      },
      ...asClasses,
      isLoading ? 'isLoading' : 'loaded'
    );
    const formFieldDataClass = classNames(
      'form__item',
      'form-text',
      className
    );
    // Autosuggest will pass through all these props to the input.
    const inputProps = {
      onChange: this.onAutoSuggestChange,
      onBlur: this.onAutoSuggestBlur,
      onKeyDown: this.onKeyPressEnter,
      className: formFieldDataClass,
      name: id,
      id,
      placeholder: replaceAll(placeholder, translations, values),
      required,
      value,
      ...subType ? { type: subType } : {},
      min: replaceAll(min, translations, values),
      max: replaceAll(max, translations, values),
      onFocus: this.onFocus,
      actionProp,
    };
    // Finally, render it!
    return (
      <div className={formFieldClass}>

        {/* Label */}
        {label &&
          <Label id={id} label={label} />
        }
        <div className="form__field--wrapper">
          <Autosuggest
            id={id}
            suggestions={suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            getSuggestionValue={this.getSuggestionValue}
            renderSuggestion={this.renderSuggestion}
            inputProps={inputProps}
            focusInputOnSuggestionClick={false}
            {...(shouldRenderSuggestions ? { shouldRenderSuggestions } : {})}
            onSuggestionSelected={this.onSuggestionSelected}
          />
          {successIcon && !errors[id] && this.isValueSet() &&
            <span className="form__validate-check">
              <Icon icon={successIcon} />
            </span>
          }
        </div>
        {/* Error Message */}
        {this.state.hints[id] &&
          <CSSTransition
            in={!!(this.state.hints[id])}
            timeout={150}
            classNames="error-slideInTop"
            unmountOnExit
          >
            <label className="form__error invalid-info" htmlFor={id}>
              <Text className="error-message" value={this.state.hints[id]} />
            </label>
          </CSSTransition>
        }
        {/* Error Message */}
        {!this.state.hints[id] && <Error
          touched={touched}
          errors={{ ...errors, ...warnings }}
          id={id}
        />}

        {/* Description */}
        {desc &&
          <Description desc={desc} />
        }

      </div>
    );
  }
}

export default compose(
  connect((state, props) => {
    // Connect props needed for this autosuggest.
    const autoSuggestObject = getAutoSuggest(props.variant);
    const {
      section: {
        packageData: {
          translations,
        },
      },
    } = state;
    const autoProps = autoSuggestObject.propsToConnect(state);
    return { autoProps, translations };
  }, (dispatch, props) => {
    // Connect actions needed for this autosuggest.
    const autoSuggestObject = getAutoSuggest(props.variant);
    return {
      actions: bindActionCreators(autoSuggestObject.actionsToConnect, dispatch),
    };
  })
)(AutoSuggest);

AutoSuggest.defaultProps = {
  placeholder: '',
  desc: null,
  label: null,
  subType: 'text',
  required: false,
  successIcon: 'checkmark',
  formValues: [],
  validators: [],
  actionProp: [],
  min: '',
  max: '',
  className: '',
};

AutoSuggest.propTypes = {
  placeholder: PropTypes.string,
  variant: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  columns: PropTypes.string.isRequired,
  setValues: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  subType: PropTypes.string,
  label: PropTypes.string,
  desc: PropTypes.string,
  required: PropTypes.bool,
  successIcon: PropTypes.string,
  formValues: PropTypes.array,
  validators: PropTypes.array,
  min: PropTypes.string,
  max: PropTypes.string,
  name: PropTypes.string.isRequired,
  actionProp: PropTypes.array,
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
};


// WEBPACK FOOTER //
// ./src/components/Steps/Input/AutoSuggest/index.jsx